import React, { useContext } from "react";
import { Source } from "react-map-gl/mapbox-legacy";
import { NON_MAPBOX_TILESET_ID } from "common/constants";
import { NODE_ENV } from "common/env";
import { CUSTOM_MAPS_SOURCE_LAYER } from "common/utils/maps/constants";
import { Tileset } from "../../../generated/graphql";
import { MIN_BOUNDS_ZOOM } from "../utils/viewportHook";
import { LayerContext } from ".";
import {
  getFillColorPolygonsLayer,
  getFillPatternPolygonsLayer,
  getFillPointsLayer,
  getLabelLinesLayer,
  getLabelPolygonsLayer,
  getLinesLayer,
  getOutlineClickPolygonsLayer,
  getOutlineHoverPolygonsLayer,
  getOutlinePointsLayer,
  getOutlinePolygonsLayer,
  getOutlineSelectedPointsLayer,
  getSelectedPointsLayer,
} from "./utils/customMaps";

const group = "customMaps";
export const CUSTOM_MAPS_SOURCE_ID_PREFIX = "custom-maps-";

const CustomMapLayer = ({
  id,
  tileset,
  labelsVisibleOnMap,
  isObject,
}: {
  id: string;
  tileset: Maybe<Tileset>;
  labelsVisibleOnMap: boolean;
  isObject: boolean;
}) => {
  const { isLayerVisible } = useContext(LayerContext);

  if (!tileset) {
    return null;
  }

  const useLocalTileData =
    NODE_ENV !== "production" && tileset.mapboxId === NON_MAPBOX_TILESET_ID;

  const visibility = isLayerVisible({ group, id }) ? "visible" : "none";
  const sourceLayer = useLocalTileData ? "src" : CUSTOM_MAPS_SOURCE_LAYER;

  return (
    <Source
      id={`${CUSTOM_MAPS_SOURCE_ID_PREFIX}${id}`}
      {...(useLocalTileData ?
        {
          tiles: [
            `${window.env.APPLICATION_URL}/api/tiles/customMaps/${id}/{z}/{x}/{y}`,
          ],
        }
      : { url: `mapbox://${tileset.mapboxId}` })}
      type="vector"
      minzoom={MIN_BOUNDS_ZOOM}
      //@ts-ignore - the TS types for mapbox gl leave a lot to be desired
      promoteId="id"
    >
      {getLinesLayer({ id, sourceLayer, visibility, isObject })}
      {getFillPointsLayer({ id, sourceLayer, visibility })}
      {getOutlinePointsLayer({ id, sourceLayer, visibility })}
      {getSelectedPointsLayer({ id, sourceLayer, visibility })}
      {getOutlineSelectedPointsLayer({ id, sourceLayer, visibility })}
      {getFillColorPolygonsLayer({ id, sourceLayer, visibility })}
      {getFillPatternPolygonsLayer({ id, sourceLayer, visibility })}
      {getOutlinePolygonsLayer({ id, sourceLayer, visibility })}
      {getLabelLinesLayer({
        id,
        sourceLayer,
        visibility:
          isLayerVisible({ group, id }) && labelsVisibleOnMap ? "visible" : (
            "none"
          ),
      })}
      {getLabelPolygonsLayer({
        id,
        sourceLayer,
        visibility:
          isLayerVisible({ group, id }) && labelsVisibleOnMap ? "visible" : (
            "none"
          ),
      })}
      {getOutlineClickPolygonsLayer({ id, sourceLayer, visibility })}
      {getOutlineHoverPolygonsLayer({ id, sourceLayer, visibility })}
    </Source>
  );
};

export default CustomMapLayer;
